import { useMemo, useState } from 'react';

import { Combobox } from '@base-ui/react/combobox';

import { useAriaId } from '@components/component-helpers';
import { FormLabel } from '@components/form-label';
import { isUserInputEdit } from '@components/select/base-ui-event';
import {
  highlightBehaviorProps,
  isSameOptionValue,
  optionLabelToString,
} from '@components/select/highlight';
import { useSelectTranslations } from '@components/select/locale';
import { NoOptions, OptionContent } from '@components/select/option-content';
import { ResultCount, usePickAnnouncement } from '@components/select/result-count';
import type { SelectOptionLabel } from '@components/select/select';
import { Item, List, Popup, Portal, Positioner } from '@components/select/styles';
import { useAsyncOptions } from '@components/select/use-async-options';
import { Stack } from '@components/stack';
import { useEvent } from '@hooks/use-event';
import { useTrackMenuOpen } from '@stores/menu-open-store';
import { toArray } from '@utils/collections';

import { ChipsControl } from './chips-control';
import { useChipsPopup } from './use-chips-popup';

interface SelectMultiAsyncOptionFields<TValue extends string> {
  value: TValue;
  helpText?: string;
  color?: string;
}

export type SelectMultiAsyncOption<TValue extends string = string> =
  SelectMultiAsyncOptionFields<TValue> & SelectOptionLabel;

type LoadOptionsCallback<TValue extends string> = (
  options: SelectMultiAsyncOption<TValue>[],
) => void;

export interface SelectMultiAsyncProps<TValue extends string = string> {
  defaultValue?: SelectMultiAsyncOption<TValue> | SelectMultiAsyncOption<TValue>[];
  onChange: (selectedOptions: SelectMultiAsyncOption<TValue>[]) => void;
  loadOptions: (
    inputValue: string,
    callback: LoadOptionsCallback<TValue>,
  ) => void | Promise<SelectMultiAsyncOption<TValue>[]>;
  defaultOptions?: boolean | SelectMultiAsyncOption<TValue>[];
  placeholder?: string;
  label: string;
  loadingMessage?: (obj: { inputValue: string }) => string | null;
  noOptions?: (obj: { inputValue: string }) => string | null;
}

// Results arrive already filtered by the server.
const acceptAll = () => true;

export function SelectMultiAsync<TValue extends string = string>(
  props: SelectMultiAsyncProps<TValue>,
) {
  const { t } = useSelectTranslations();
  const {
    defaultValue,
    onChange,
    loadOptions,
    defaultOptions,
    placeholder = t('placeholder'),
    label,
    loadingMessage = () => t('loading'),
    noOptions = () => t('no_options'),
  } = props;

  const selectId = useAriaId('selectmulti-async');
  const [selected, setSelected] = useState<SelectMultiAsyncOption<TValue>[]>(toArray(defaultValue));
  const { inputValue, setInputValue, isOpen, setIsOpen, handleOpenChange } = useChipsPopup(() =>
    search(''),
  );

  const { items, isLoading, emptyMessage, search } = useAsyncOptions({
    loadOptions,
    defaultOptions,
    isOpen,
    inputValue,
    noOptions,
    loadingMessage,
  });

  const pick = usePickAnnouncement(isOpen);

  const handleValueChange = useEvent((next: SelectMultiAsyncOption<TValue>[]) => {
    const added = next.find((option) => !selected.some((item) => item.value === option.value));
    const removed = selected.find((item) => !next.some((option) => option.value === item.value));
    setSelected(next);
    onChange(next);
    if (added) pick.announcePick(optionLabelToString(added));
    if (removed) pick.announceRemoval(optionLabelToString(removed));
  });

  const handleInputValueChange = useEvent((next: string, details: { reason: string }) => {
    setInputValue(next);
    if (isUserInputEdit(details.reason)) {
      pick.clearPick();
      search(next);
    }
  });

  useTrackMenuOpen(isOpen);

  // Selected options render as chips in the control, so hide them from the
  // list itself.
  const visibleItems = useMemo(
    () => items.filter((option) => !selected.some((item) => item.value === option.value)),
    [items, selected],
  );

  return (
    <Stack space={1}>
      <FormLabel text={label} htmlFor={selectId} />
      <Combobox.Root<SelectMultiAsyncOption<TValue>, true>
        items={visibleItems}
        multiple
        value={selected}
        open={isOpen}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        inputValue={inputValue}
        onInputValueChange={handleInputValueChange}
        filter={acceptAll}
        isItemEqualToValue={isSameOptionValue}
        itemToStringLabel={optionLabelToString}
        {...highlightBehaviorProps}
      >
        <ChipsControl
          inputId={selectId}
          selected={selected}
          placeholder={placeholder}
          isOpen={isOpen}
          onOpen={() => setIsOpen(true)}
          hasError={false}
        />
        <ResultCount
          isAnnouncing={isOpen && !isLoading}
          pickedLabel={pick.pickedLabel}
          removedLabel={pick.removedLabel}
        />

        <Portal>
          <Positioner align="start">
            <Popup $isHiddenWhenEmpty={emptyMessage === null}>
              {emptyMessage !== null && <NoOptions>{emptyMessage}</NoOptions>}
              <List>
                {(item: SelectMultiAsyncOption<TValue>) => (
                  <Item key={item.value} value={item}>
                    <OptionContent label={item.label} helpText={item.helpText} hasReturnHint />
                  </Item>
                )}
              </List>
            </Popup>
          </Positioner>
        </Portal>
      </Combobox.Root>
    </Stack>
  );
}
